import React from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'

// Components
import AnimateScaleIn from '../animation/AnimateScaleIn'
import AnimateSlideText from '../animation/AnimateSlideText'
import Button from '../Button'
import PageTransitionLink from '../PageTransitionLink'
import ContainedWrap from '../ContainedWrap'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import ImageLabel from '../ImageLabel'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Styles
import {
  ImageContentSplitMain,
  ImageContentSplitImage,
  ImageContentSplitSecondaryImageWrap,
  ImageContentSplitSecondaryImage,
} from './index.style'
import { colors } from '../../styles/vars/colors.style'

const ImageContentSplit = ({
  id,
  color,
  text,
  images,
  button1,
  button2,
  flip,
  secondaryImagePos,
  linkId,
}) => {
  return (
    <ScrollSection>
      <ImageContentSplitMain bg={color} id={linkId && linkId}>
        <Container>
          <Grid>
            <GridItem
              tiny={12}
              tinyOrder={2}
              tinyAlign={`flex-end`}
              tabletP={10}
              tabletPStart={2}
              tabletL={5}
              tabletLStart={flip ? 8 : 1}
              desk={6}
              deskStart={flip ? 7 : 1}
            >
              <Spacer size={[0, 48]} />

              <ImageContentSplitImage>
                <AnimateScaleIn id={id} position={'relative'}>
                  <GatsbyImage
                    image={images[0].gatsbyImageData}
                    alt={images[0].description}
                    width={900}
                    height={1032}
                  />
                </AnimateScaleIn>
                {images[0].title && (
                  <ImageLabel id={`${id}-label`} imageLabel={images[0].title} />
                )}
              </ImageContentSplitImage>
            </GridItem>
            <GridItem
              tiny={12}
              tinyOrder={1}
              tinyAlign={`center`}
              tabletP={10}
              tabletPStart={2}
              tabletL={6}
              tabletLStart={flip ? 1 : 7}
              tabletLOrder={flip ? -1 : 2}
              desk={4}
              deskStart={flip ? 2 : 8}
            >
              {flip && images.length > 1 && secondaryImagePos === 'Alt' && (
                <ImageContentSplitSecondaryImageWrap flip={true}>
                  {flip && <Spacer size={[63, 48]} />}
                  <ImageContentSplitSecondaryImage>
                    <AnimateScaleIn
                      id={id + '-secondary'}
                      position={'relative'}
                    >
                      <GatsbyImage
                        image={images[1].gatsbyImageData}
                        alt={images[1].description}
                        width={284}
                        height={200}
                      />
                    </AnimateScaleIn>
                  </ImageContentSplitSecondaryImage>
                  {flip && <Spacer size={48} />}
                </ImageContentSplitSecondaryImageWrap>
              )}
              <ContainedWrap size={600} center={false}>
                <Spacer size={[54, 48]} />
                <RichText id={`${id}-text`} content={text} />

                {button1 && (
                  <>
                    <Spacer size={[30, 52]} />
                    <AnimateSlideText id={`${id}-cta`}>
                      <PageTransitionLink
                        to={`/expeditions/${button1.internalLink.slug}${
                          button1.anchorIdOptional
                            ? `?` + button1.anchorIdOptional
                            : ``
                        }`}
                        mask={false}
                      >
                        <Button
                          variant="underline"
                          color={colors.darkBlue}
                          inline
                        >
                          {button1.label}
                        </Button>
                      </PageTransitionLink>
                    </AnimateSlideText>
                  </>
                )}
                {button2 && (
                  <>
                    <Spacer size={[10, 22]} />
                    <AnimateSlideText id={`${id}-cta-2`} delay={0.1}>
                      <PageTransitionLink
                        to={`/${button2.internalLink.slug}${
                          button2.anchorIdOptional
                            ? `?` + button2.anchorIdOptional
                            : ``
                        }`}
                        mask={false}
                      >
                        <Button
                          variant="underline"
                          color={colors.darkBlue}
                          inline
                        >
                          {button2.label}
                        </Button>
                      </PageTransitionLink>
                    </AnimateSlideText>
                  </>
                )}
                <Spacer size={[63, 48]} />
              </ContainedWrap>
              {images.length > 1 &&
                (!flip || (flip && secondaryImagePos === 'Normal')) && (
                  <ImageContentSplitSecondaryImageWrap
                    flip={flip || secondaryImagePos === 'Alt'}
                  >
                    {flip && <Spacer size={48} />}
                    <ImageContentSplitSecondaryImage>
                      <AnimateScaleIn
                        id={id + '-secondary'}
                        position={'relative'}
                      >
                        <GatsbyImage
                          image={images[1].gatsbyImageData}
                          alt={images[1].description}
                          width={284}
                          height={200}
                        />
                      </AnimateScaleIn>
                    </ImageContentSplitSecondaryImage>
                  </ImageContentSplitSecondaryImageWrap>
                )}
            </GridItem>
          </Grid>
        </Container>
      </ImageContentSplitMain>
    </ScrollSection>
  )
}

ImageContentSplit.propTypes = {
  id: PropTypes.string.isRequired,
  color: PropTypes.string,
  text: PropTypes.object,
  button1: PropTypes.object,
  button2: PropTypes.object,
  images: PropTypes.array,
  flip: PropTypes.bool,
  secondaryImagePos: PropTypes.oneOf(['Normal', 'Alt']),
}

ImageContentSplit.defaultProps = {
  flip: false,
  secondaryImagePos: 'Normal',
}

export default ImageContentSplit
